// Floating / anchored tree-row context menu. The floating variant portals
// to <body> with z-index 9999 so it escapes every parent stacking context
// (sticky search bar z=5, snip cards z=2, appbar z=60). The anchored variant
// is rendered inline beneath the ⋯ button.

import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useClampedMenuPosition } from './usePortalPosition';

export interface TreeMenuAction {
  id: string;
  label: string;
  icon?: ReactNode;
  danger?: boolean;
  kbd?: string;
  divider?: boolean; // if true, render a divider BEFORE this item
}

interface BaseProps {
  actions: TreeMenuAction[];
  onAction: (actionId: string) => void;
  onClose: () => void;
}

interface FloatingProps extends BaseProps {
  variant: 'float';
  pos: { x: number; y: number };
  /** Element to refocus when the menu closes. */
  returnFocusTo?: HTMLElement | null;
  /** Optional scroll container — closing on scroll keeps the menu honest. */
  scrollContainer?: HTMLElement | null;
}

interface AnchoredProps extends BaseProps {
  variant: 'anchor';
}

export type Props = FloatingProps | AnchoredProps;

export function TreeRowContextMenu(props: Props) {
  const menuRef = useRef<HTMLDivElement>(null);
  const clampedPos = useClampedMenuPosition(
    menuRef,
    props.variant === 'float' ? props.pos : null,
  );

  // Outside-mousedown + Escape + scroll closes.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        props.onClose();
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        props.onClose();
      }
    }
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);

    let scroller: HTMLElement | null = null;
    if (props.variant === 'float' && props.scrollContainer) {
      scroller = props.scrollContainer;
      const onScroll = () => props.onClose();
      scroller.addEventListener('scroll', onScroll, { passive: true });
      return () => {
        document.removeEventListener('mousedown', onDown, true);
        document.removeEventListener('keydown', onKey, true);
        scroller?.removeEventListener('scroll', onScroll);
      };
    }
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [props]);

  // Focus the first menu item on mount; return focus on unmount.
  useEffect(() => {
    const first = menuRef.current?.querySelector<HTMLButtonElement>('button[role="menuitem"]');
    first?.focus();
    return () => {
      if (props.variant === 'float' && props.returnFocusTo) {
        props.returnFocusTo.focus?.();
      }
    };
  }, [props]);

  // Arrow-key navigation.
  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (!menuRef.current) return;
    const items = Array.from(
      menuRef.current.querySelectorAll<HTMLButtonElement>('button[role="menuitem"]'),
    );
    if (!items.length) return;
    const active = document.activeElement as HTMLElement | null;
    const idx = active ? items.indexOf(active as HTMLButtonElement) : -1;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      items[(idx + 1) % items.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      items[(idx - 1 + items.length) % items.length]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      items[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      items[items.length - 1]?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      // default button behavior handles activation
    }
  }

  const body = (
    <div
      ref={menuRef}
      role="menu"
      className={`ws-tnode__pop${props.variant === 'float' ? ' is-float' : ''}`}
      style={
        props.variant === 'float' && clampedPos
          ? { left: clampedPos.x, top: clampedPos.y }
          : undefined
      }
      onKeyDown={handleKeyDown}
      onContextMenu={(e) => e.preventDefault()}
    >
      {props.actions.map((a, i) => (
        <span key={a.id}>
          {a.divider && i > 0 && <div className="ws-tnode__pop-divider" aria-hidden />}
          <button
            type="button"
            role="menuitem"
            className={a.danger ? 'is-danger' : undefined}
            onClick={() => { props.onAction(a.id); props.onClose(); }}
          >
            {a.icon}
            <span>{a.label}</span>
            {a.kbd && <span className="kbd">{a.kbd}</span>}
          </button>
        </span>
      ))}
    </div>
  );

  if (props.variant === 'float') {
    return createPortal(body, document.body);
  }
  return body;
}
